/**
 * Product analytics (PostHog) for desktop data entry (DDE).
 *
 * Same PostHog projects as the dashboard (`baoleaders-frontend/src/index.tsx`):
 * the production key for production builds, the development key otherwise.
 * Every event carries `app: 'dde'` and the stage this build talks to, so DDE
 * events can be told apart from the dashboard's and UAT from prod.
 *
 * Privacy: people are identified by their numeric user id only. Never pass a
 * phone number, email, name, free text or a raw error message as a property —
 * use ids, counts and the coarse `errorCode()` instead. Autocapture and session
 * recording are off: collectors type community members' details into DDE.
 *
 * Analytics must never break the app: every call is a no-op until
 * `initAnalytics()` has run, and every failure is swallowed.
 */
import posthog from 'posthog-js'

const PROD_KEY = 'phc_FJTHAtI4Nc2AAKfrfGp2le7PGuxTQ7fDeKVxWeJutXx'
const DEV_KEY = 'phc_buOFrinwCnvsUE8yMNFqYmij6a7h0sEeVuU9UuWelBV'
const HOST = 'https://app.posthog.com'

export type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>

type AnalyticsClient = Pick<
  typeof posthog,
  'init' | 'register' | 'identify' | 'reset' | 'capture' | 'get_distinct_id'
>

let client: AnalyticsClient | null = null
/** Properties every event carries; registered again after a logout reset. */
let superProperties: Record<string, string> = {}

/** URL properties PostHog records on its own (page views, first visit, referrer). */
const URL_PROPERTIES = [
  '$current_url',
  '$initial_current_url',
  '$referrer',
  '$initial_referrer',
  '$session_entry_url'
]

const withoutQuery = (value: unknown) =>
  typeof value === 'string' ? value.split(/[?#]/)[0] : value

/**
 * Drop the query string and fragment from every URL PostHog would send. The
 * invite SMS links here with `?firstName=…&lastName=…&code=<set-password
 * code>`, and the reset-password page takes `?code=`, so a page view on either
 * would have sent a live password code and the collector's name to PostHog.
 */
export const scrubUrlProperties = <T extends Record<string, any> | null>(event: T): T => {
  if (!event) return event
  ;['properties', '$set', '$set_once'].forEach((bag) => {
    const values = (event as any)[bag]
    if (!values || typeof values !== 'object') return
    URL_PROPERTIES.forEach((key) => {
      if (key in values) values[key] = withoutQuery(values[key])
    })
  })
  return event
}

export const getAnalyticsKey = (nodeEnv = process.env.NODE_ENV) =>
  nodeEnv === 'production' ? PROD_KEY : DEV_KEY

/** `prod`, `uat`, `dev` or `local`, from the API host this build talks to. */
export const stageFromApiHost = (apiHost?: string) => {
  const host = (apiHost || '').toLowerCase()
  if (/prod/.test(host)) return 'prod'
  if (/uat/.test(host)) return 'uat'
  if (/dev/.test(host)) return 'dev'
  return 'local'
}

export const initAnalytics = ({ apiHost }: { apiHost?: string } = {}) => {
  if (client) return
  try {
    posthog.init(getAnalyticsKey(), {
      api_host: HOST,
      autocapture: false,
      disable_session_recording: true,
      capture_pageview: true,
      before_send: scrubUrlProperties
    })
    superProperties = { app: 'dde', stage: stageFromApiHost(apiHost) }
    posthog.register(superProperties)
    client = posthog
  } catch (error) {
    client = null
  }
}

/** Link this browser's events to a person. Only the user id is sent. */
export const identifyUser = (userId?: string | number | null) => {
  if (!client || userId === undefined || userId === null || `${userId}` === '') return
  try {
    if (client.get_distinct_id() === `${userId}`) return
    client.identify(`${userId}`)
  } catch (error) {
    // never let analytics break the app
  }
}

/** Forget the person on logout so the next collector on a shared computer starts fresh. */
export const resetAnalytics = () => {
  if (!client) return
  try {
    client.reset()
    // reset() also clears registered properties, so events after a logout lost
    // `app` and `stage` until the app was reloaded.
    client.register(superProperties)
  } catch (error) {
    // never let analytics break the app
  }
}

export const captureEvent = (event: string, properties: AnalyticsProperties = {}) => {
  if (!client) return
  try {
    const clean: Record<string, string | number | boolean | null> = {}
    Object.keys(properties).forEach((key) => {
      const value = properties[key]
      if (value !== undefined) clean[key] = value
    })
    client.capture(event, clean)
  } catch (error) {
    // never let analytics break the app
  }
}

/**
 * A coarse, PII-free classification of an error for event properties: an HTTP
 * status, `network`, `timeout`, `graphql` or `unknown`. Never the message.
 */
export const errorCode = (error: unknown): string => {
  const anyError = error as any
  const status = anyError?.response?.status ?? anyError?.networkError?.statusCode
  if (typeof status === 'number') return `http_${status}`
  if (anyError?.graphQLErrors?.length) return 'graphql'

  const message = `${anyError?.message ?? anyError ?? ''}`
  const statusInMessage = message.match(/status(?: code)? (\d{3})\b/i)
  if (statusInMessage) return `http_${statusInMessage[1]}`
  if (/timed? ?out|timeout/i.test(message)) return 'timeout'
  if (/network|internet|offline|connection|failed to fetch/i.test(message)) return 'network'
  return 'unknown'
}

/** Test-only: inject a fake client. */
export const __setAnalyticsClientForTests = (
  fake: AnalyticsClient | null,
  registered: Record<string, string> = {}
) => {
  client = fake
  superProperties = registered
}
